import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"
import { AnimatePresence } from "framer-motion"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useRef, useState } from "react"


import { MessageList } from "@/components/Chat/ChatStyles.ts"
import { LoadMoreButton } from "@/components/Chat/LoadMoreButton.tsx"
import { MessageContainerWithActionsPopup } from "@/components/Chat/MessageContainerWithActionsPopup.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { DotsProgress } from "@/components/DotsProgress/DotsProgress.tsx"
import { useScrollableModel } from "@/components/Scrollable/ScrollableContext.ts"
import { useElementHeight } from "@/hooks/useElementHeight.ts"
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId } from "@/routes/projects/projectHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { CreatePrDialog } from "@/routes/tasks/TaskViewScene/CreatePrDialog.tsx"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ChatMode } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { IObservableValue } from "mobx"
import type { PropsWithChildren, RefObject } from "react"

export const Chat = observer((props: {
  model: TaskViewModel
  isAgentRunning: IObservableValue<boolean>
  inputContainerRef: RefObject<HTMLDivElement | null>
  resizable: boolean
}) => {
  const navigate = useNavigate()
  const orgId = useCurrentOrgId()

  const handleMessagesVisibility = useCallback((entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const messageId = entry.target.getAttribute("data-message-id")
        if (messageId) {
          props.model.chatModel.read(messageId)
        }
      }
    }
  }, [props.model])

  const messages = props.model.chatModel.messages.get()

  const me = props.model.me.get()

  const [prDialogOpen, setPrDialogOpen] = useState(false)
  const pendingEditConfirmation = props.model.chatModel.pendingEditConfirmation.get()

  // Do not show "Load more" buttons if there are no rendered messages
  const [hasMountedMessages, setHasMountedMessages] = useState(false)
  useEffect(() => setHasMountedMessages(messages.length > 0), [messages.length])

  const observer = useIntersectionObserver(handleMessagesVisibility)

  const loadMorePressed = useRef(false)

  const scrollableModel = useScrollableModel()

  const isAutoScrollEnabledRef = useRef(true)
  const inputContainerHeight = useElementHeight(props.inputContainerRef, 120)

  const task = props.model.task.get()

  const containerElement = scrollableModel.containerElement.get()

  useEffect(() => {
    if (!containerElement) return

    const handleScroll = () => {
      const distanceFromBottom = containerElement.scrollHeight - containerElement.scrollTop - containerElement.clientHeight
      isAutoScrollEnabledRef.current = distanceFromBottom <= 50
    }

    containerElement.addEventListener("wheel", handleScroll)
    return () => containerElement.removeEventListener("wheel", handleScroll)
  }, [containerElement])

  useEffect(() => {
    const lastMessageIsMine = messages.length > 0 && messages[messages.length - 1].isMyMessage

    if (!loadMorePressed.current && (isAutoScrollEnabledRef.current || lastMessageIsMine)) {
      scrollableModel.scrollToBottom()
    }

    loadMorePressed.current = false
  }, [messages, scrollableModel])

  useEffect(() => {
    if (isAutoScrollEnabledRef.current) {
      scrollableModel.scrollToBottom()
    }
  }, [inputContainerHeight, scrollableModel])

  if (props.model.chatModel.isResettingCheckpoint.get()) {
    return <ResettingCheckpointState>{ellipsize("Resetting checkpoint")}</ResettingCheckpointState>
  }

  return (
    <ChatContainer inputContainerHeight={inputContainerHeight} resizable={props.resizable}>
      {hasMountedMessages && (
        <LoadMoreButton model={props.model.chatModel} loadNext={false} loadMorePressed={loadMorePressed} />
      )}

      <MessageListAnimated visible={messages.length > 0}>
        {messages.map(message => (
          <MessageContainerWithActionsPopup
            key={message.id}
            observer={observer}
            message={message}
            isAgentRunning={props.isAgentRunning}
            currentVersionCommitId={props.model.currentVersionCommitId}
            successfulVersionCommitIds={props.model.successfulVersionCommitIds}
            gitHubVersionInfo={props.model.gitHubModel.gitHubVersionInfo(() => {
              setPrDialogOpen(true)
            })}
            runningProcess={task?.runningProcess ?? null}
            onVersionCheckout={(commitId) =>
              props.model.checkout(commitId).then(() =>
                navigate(Navigator.task(orgId, props.model.project.get().key, props.model.taskId, { view: TaskViewId.Full }))
              )
            }
            onMarkSuccessful={(commitId, successful) =>
              props.model.markAsSuccessful(commitId, successful)
            }
          />
        ))}
      </MessageListAnimated>

      <WaitingForResponse
        isReady={props.model.chatModel.isReady}
        chatMode={props.model.chatMode.get()}
        waitingForResponse={props.model.chatModel.waitingForResponse}
        autoscrollEnabled={isAutoScrollEnabledRef.current}
      />

      {hasMountedMessages && (
        <LoadMoreButton model={props.model.chatModel} loadNext loadMorePressed={loadMorePressed} />
      )}

      {task && me && (
        <CreatePrDialog
          isOpen={prDialogOpen}
          onOpenChange={setPrDialogOpen}
          taskModel={props.model}
          taskLink={props.model.taskLink}
          task={task}
          author={me}
          historyItems={props.model.historyItems.get()}
        />
      )}

      {pendingEditConfirmation && (
        <ConfirmDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              props.model.chatModel.cancelEditConfirmation()
            }
          }}
          trigger={<div />}
          description={`Editing this message will rollback ${pendingEditConfirmation.commits} version${pendingEditConfirmation.commits === 1 ? "" : "s"}. Are you sure you want to continue?`}
          confirmText="Rollback and Edit"
          confirmKind="danger"
          onConfirm={() => {
            props.model.chatModel.confirmEditWithRollback()
          }}
        />
      )}
    </ChatContainer>
  )
})

const MessageListAnimated = (props: PropsWithChildren<{
  visible: boolean
}>) => {
  return (
    <AnimatePresence>
      {props.visible && (
        <MessageList
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          data-testid="chat-message-list"
        >
          {props.children}
        </MessageList>
      )}
    </AnimatePresence>
  )
}

const WaitingForResponse = observer((props: {
  isReady: IObservableValue<boolean>
  chatMode: ChatMode | undefined
  waitingForResponse: IObservableValue<number>
  autoscrollEnabled: boolean
}) => {
  const isVisible = props.waitingForResponse.get() > 0 && props.chatMode == "UserToAgent"
  const scrollableModel = useScrollableModel()

  // Scroll to the bottom once on mount, then scroll again when waitingForResponse changes
  const isReady = props.isReady.get()
  const waitingForResponse = props.waitingForResponse.get()

  useEffect(() => {
    if (isReady && props.autoscrollEnabled) {
      scrollableModel.scrollToBottom("instant")
    }
  }, [isReady, props.autoscrollEnabled, scrollableModel])

  useEffect(() => {
    if (!isVisible) return

    const timer = setTimeout(() => {
      if (props.autoscrollEnabled) {
        scrollableModel.scrollToBottom()
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [isVisible, props.autoscrollEnabled, scrollableModel, waitingForResponse])

  return (
    <DotsContainer visible={isVisible}>
      <DotsProgress ref={null} />
    </DotsContainer>
  )
})

const ChatContainer = styled.div<{ inputContainerHeight: number, resizable: boolean }>`
  width: 100%;
  max-width: calc(var(--chat-width) + 64px);
  margin: 0 auto;
  padding: 0 32px ${props => props.inputContainerHeight}px ${props => props.resizable ? "0" : "32px"};
`

const ResettingCheckpointState = styled.div`
  width: 100%;
  height: 100%;

  flex-direction: column;
  flex: 1 1;

  align-items: center;
  justify-content: center;
`

const DotsContainer = styled.div<{ visible: boolean }>`
  max-width: var(--chat-width);
  margin-bottom: 16px;

  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? "visible" : "hidden"};

  transition: opacity 0.3s ease-in-out;
`
